import {
  ItemSelectV5Drawer,
  ItemV5,
  SearchScene,
} from '@knowledge/common/components/ItemSelectV5/Index';
import { DrawerProps, Button } from 'antd';
import { useTranslate } from '@tolgee/react';
import {
  SupplementalKnowledgeBindItem,
  SupplementKnowledgeData,
} from 'services/api/supplementKnowledge';
import { formatSpuList } from '../utils';
import { useRef, useState } from 'react';
import { useUpdateExpandSpu } from './useUpdateExpandSpu';
import { PlusOutlined } from '@ant-design/icons';
import { uniqBy } from 'lodash-es';

export const item2bindItem = ({
  imageUrl,
  internalItemId,
  platform,
  region,
  externalShopId,
  externalItemId,
  itemName,
  skuInfo,
}: ItemV5): SupplementalKnowledgeBindItem => ({
  imageUrl: imageUrl[0],
  itemInternalId: Number(internalItemId),
  platform,
  region,
  spuId: externalItemId,
  shopId: externalShopId,
  title: itemName,
  skus: skuInfo.map(({ skuId, skuName, sellerSku }) => ({
    skuId,
    skuName,
    sellerSku,
  })),
});

interface ExpandSkuDrawerProps extends DrawerProps {
  knowledgeId: SupplementKnowledgeData['id'];
  bindItem?: SupplementalKnowledgeBindItem[];
  onBindItemChange?: (bindItem: SupplementalKnowledgeBindItem[]) => void;
}

const bodyStyle = { backgroundColor: '#F7F9FA', padding: 16 };

const MoreSkuDrawer = (
  props: DrawerProps & {
    knowledgeId: number;
    onAddBindItem?: (bindItem: SupplementalKnowledgeBindItem[]) => void;
    onRefresh?: () => void;
  },
) => {
  const { knowledgeId, onAddBindItem, onRefresh, ...rest } = props;
  const { updateSpuMutation, isLoading } = useUpdateExpandSpu(knowledgeId);
  const { t } = useTranslate();

  return (
    <ItemSelectV5Drawer
      showSpuClassSelect
      showSameSellerSkuTag={false}
      destroyOnClose
      title={t('新增扩展商品')}
      width={980}
      bodyStyle={bodyStyle}
      {...rest}
      defaultParams={{
        knowledgeId,
        scene: SearchScene.ExpandSeed,
      }}
      renderFooter={(items) => {
        return (
          <div className="flex items-center justify-end gap-4">
            <span className="text-[#84868C]">{t('voucher.已选x', { x: items.length })}</span>
            <Button onClick={() => props.onClose?.({} as any)}>{t('取消')}</Button>
            <Button
              type="primary"
              loading={isLoading}
              onClick={async () => {
                if (items.length === 0) {
                  return;
                }

                if (onAddBindItem) {
                  onAddBindItem(items.map(item2bindItem));
                  props.onClose?.({} as any);
                  return;
                }

                await updateSpuMutation({
                  action: 'ADD',
                  spuList: formatSpuList(items),
                });
                onRefresh?.();
              }}
            >
              {t('voucher.确认选择')}
            </Button>
          </div>
        );
      }}
    />
  );
};

export const ExpandSkuDrawer = (props: ExpandSkuDrawerProps) => {
  const { knowledgeId, bindItem, onBindItemChange, ...rest } = props;
  const { t } = useTranslate();

  const [expandSkuVisible, setExpandSkuVisible] = useState(false);
  const refreshRef = useRef<() => void>();

  const { updateSpuMutation, isLoading: updateSpuLoading } = useUpdateExpandSpu(knowledgeId);

  const showExpandSku = () => {
    setExpandSkuVisible(true);
  };
  const hideExpandSku = () => {
    setExpandSkuVisible(false);
    refreshRef.current = undefined;
  };

  return (
    <>
      <ItemSelectV5Drawer
        showSpuClassSelect
        showSameSellerSkuTag={false}
        bindItem={bindItem}
        defaultParams={{
          knowledgeId,
          scene: SearchScene.EditSkuGroup,
        }}
        title={t('扩展关联商品详情')}
        width={1080}
        bodyStyle={bodyStyle}
        {...rest}
        destroyOnClose
        renderTitleExtra={(_, resetItems) => (
          <Button
            type="primary"
            onClick={() => {
              showExpandSku();
              refreshRef.current = resetItems;
            }}
          >
            <PlusOutlined />
            {t('新增扩展商品')}
          </Button>
        )}
        renderFooter={(items, resetItems) => {
          return (
            <div className="flex items-center justify-end gap-4">
              <span className="text-[#84868C]">{t('voucher.已选x', { x: items.length })}</span>
              <Button
                onClick={() => {
                  rest.onClose?.({} as any);
                }}
              >
                {t('取消')}
              </Button>
              <Button
                type="primary"
                loading={updateSpuLoading}
                onClick={async () => {
                  if (items.length === 0) {
                    return;
                  }
                  if (onBindItemChange) {
                    const removeBindItem = items.map(item2bindItem);
                    const nextBindItem = bindItem
                      ?.map((cur) => {
                        const target = removeBindItem.find(({ spuId }) => spuId === cur.spuId);
                        if (!target) {
                          return cur;
                        }
                        const skus = cur.skus.filter((_) =>
                          target.skus.every(({ skuId }) => skuId !== _.skuId),
                        );
                        if (!skus.length && cur.skus.length) {
                          return undefined;
                        }
                        return {
                          ...cur,
                          skus,
                        };
                      })
                      .filter((_) => _) as SupplementalKnowledgeBindItem[];
                    onBindItemChange(nextBindItem);
                    return;
                  }
                  await updateSpuMutation({
                    action: 'REMOVE',
                    spuList: formatSpuList(items),
                  });
                  resetItems();
                }}
              >
                {t('剔除选中的关联商品')}
              </Button>
            </div>
          );
        }}
      />
      {expandSkuVisible && (
        <MoreSkuDrawer
          visible={expandSkuVisible}
          knowledgeId={knowledgeId}
          onRefresh={() => {
            refreshRef.current?.();
            hideExpandSku();
          }}
          onClose={hideExpandSku}
          onAddBindItem={
            onBindItemChange
              ? (added) => {
                  const nextBindItem = added.reduce(
                    (res, cur) => {
                      const targetIndex = res.findIndex(({ spuId }) => spuId === cur.spuId);
                      if (targetIndex < 0) {
                        res.push(cur);
                      } else {
                        res[targetIndex] = {
                          ...res[targetIndex],
                          skus: uniqBy([...res[targetIndex].skus, ...cur.skus], 'skuId'),
                        };
                      }
                      return res;
                    },
                    [...(bindItem || [])],
                  );
                  onBindItemChange(nextBindItem);
                }
              : undefined
          }
        />
      )}
    </>
  );
};
